'use client';

import {QueryClient} from '@components/@query';
import {useQuestionsStore} from '@components/LiveEvent/@hooks';
import {MutationKey} from '@config/mutationKeys';
import {QueryKey} from '@config/queryKeys';
import {
    IQuestionsResponse,
    QuestionsSortByEnum,
} from '@services/live-event/live-event.model';
import LiveEventService from '@services/live-event/live-event.service';
import {useMutation} from '@tanstack/react-query';
import {useCurrentLiveEventId} from './useCurrentLiveEventId';
import {useCurrentLiveEventSlug} from './useCurrentLiveEventSlug';

export const useQuestionMutation = () => {
    const eventSlug = useCurrentLiveEventSlug();
    const eventId = useCurrentLiveEventId();
    const filter = useQuestionsStore(s => s.sortByFilter);
    const question = useQuestionsStore(s => s.questionText);
    const updateQuestionField = useQuestionsStore(s => s.updateQuestionText);
    const updateFilter = useQuestionsStore(s => s.updateSortByFilter);
    const addNewQuestion = useQuestionsStore(s => s.updateNewQuestionText);

    const mutation = useMutation({
        mutationKey: MutationKey.LIVE_EVENT_QUESTION_CREATE(eventId),
        mutationFn: async () => {
            return LiveEventService.createQuestion(eventId, question!.trim());
        },
        onMutate: async () => {
            addNewQuestion(question.trim());

            const previousQuestions =
                QueryClient.getQueryData<IQuestionsResponse>(
                    QueryKey.LIVE_EVENT_QUESTIONS(eventId!, filter),
                );

            return {previousQuestions};
        },
        onError: (_, __, context) => {
            if (!context) {
                return;
            }

            QueryClient.setQueryData(
                QueryKey.LIVE_EVENT_QUESTIONS(eventId, filter),
                context.previousQuestions,
            );
        },

        onSuccess: async () => {
            updateQuestionField('');
            await QueryClient.refetchQueries({
                queryKey: QueryKey.LIVE_EVENT_QUESTIONS(
                    eventId,
                    QuestionsSortByEnum.NEWEST_FIRST,
                ),
            });
            updateFilter(QuestionsSortByEnum.NEWEST_FIRST);
        },

        onSettled: async () => {
            Object.values(QuestionsSortByEnum).forEach(sortOrder => {
                QueryClient.invalidateQueries({
                    queryKey: QueryKey.LIVE_EVENT_QUESTIONS(
                        eventId!,
                        sortOrder,
                    ),
                });
            });
            await QueryClient.invalidateQueries({
                queryKey: QueryKey.LIVE_EVENT(eventSlug),
            });
        },
    });

    return {
        mutation,
    };
};
